"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
// /trendi — landing page for the Trendi AI persona.
// Dark theme matching the main site, brand palette (teal/orange), framer-motion throughout.
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Brain,
  MessageCircle,
  ShieldCheck,
  Zap,
  Bell,
  TrendingUp,
  ArrowLeft,
  Heart,
  Lightbulb,
  Lock,
  Rocket,
  BarChart3,
  Clock,
  Globe2,
  Check,
  X,
  ChevronDown,
  Instagram,
  Plus,
  Layers,
  Eye,
  Languages,
  Palette,
  Wand2,
  Activity,
  Star,
  Quote,
  PlayCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import { TrendyProvider, useAiPersona, ACCENT_HEX, ACCENT_HEX_SOFT } from "@/lib/trendy";
import { TrendiMascot } from "@/components/trendy/TrendiMascot";

const TRENDI_LINKS = [
  { id: "meet", label: "آشنایی" },
  { id: "personality", label: "شخصیت" },
  { id: "abilities", label: "توانایی‌ها" },
  { id: "how", label: "چطور کار می‌کنه" },
  { id: "demo", label: "دموی زنده" },
  { id: "faq", label: "پرسش‌ها" },
];

function TrendiPageWrapper() {
  return (
    <TrendyProvider>
      <TrendiPage />
    </TrendyProvider>
  );
}

export default function TrendiPage() {
  return (
    <div
      dir="rtl"
      className="min-h-screen bg-background text-foreground overflow-x-clip selection:bg-primary-main selection:text-white"
    >
      <SiteHeader hashMode links={TRENDI_LINKS} />

      <main className="relative">
        <Hero />
        <StatsBar />
        <MeetTrendi />
        <Personality />
        <Abilities />
        <HowItWorks />
        <LiveConversation />
        <WhiteLabel />
        <PersonaStudio />
        <Comparison />
        <Trust />
        <FAQ />
        <FinalCTA />
      </main>

      <SiteFooter />
    </div>
  );
}

/* ============================================================
   HERO
   ============================================================ */
function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMascot = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const { persona } = useAiPersona();

  return (
    <section
      ref={ref}
      className="relative min-h-[92vh] pt-32 pb-20 overflow-hidden flex items-center"
    >
      {/* calm aurora background — single soft mesh, no animated blobs */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 h-[700px] w-[1100px] opacity-50 blur-[120px]"
          style={{
            background:
              "radial-gradient(ellipse at 50% 35%, rgba(0,150,136,.45), rgba(254,150,74,.18) 45%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(currentColor_1px,transparent_1px),linear-gradient(90deg,currentColor_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_1fr] items-center gap-14 w-full">
        {/* Copy column */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative z-10"
        >
          <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-bold text-primary-main border border-primary-main/30 bg-primary-main/10 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-primary-main animate-ping opacity-60" />
              <span className="relative h-2 w-2 rounded-full bg-primary-main" />
            </span>
            دستیار هوشمند داخل ترندی اسمارت
          </span>

          <h1 className="mt-6 text-5xl md:text-[80px] font-black leading-[0.98] tracking-tight">
            <span className="block">سلام،</span>
            <span className="block mt-2">
              من{" "}
              <span className="relative inline-block">
                <span className="brand-gradient-text">ترندی</span>
                <motion.svg
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 0.6, duration: 1.2, ease: "easeInOut" }}
                  viewBox="0 0 200 14"
                  className="absolute -bottom-3 right-0 left-0 h-3 w-full"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M2 10 Q 50 2, 100 8 T 198 6"
                    stroke="url(#trendi-underline)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    fill="none"
                  />
                  <defs>
                    <linearGradient
                      id="trendi-underline"
                      x1="0"
                      x2="200"
                      y1="0"
                      y2="0"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor="rgb(0 150 136)" />
                      <stop offset="100%" stopColor="rgb(254 150 74)" />
                    </linearGradient>
                  </defs>
                </motion.svg>
              </span>
              <span className="text-foreground/50">‌ام</span>
            </span>
          </h1>

          <p className="mt-8 max-w-xl text-base md:text-lg leading-9 text-muted-foreground">
            یه ابزار خشک نیستم — یه شخصیت زنده‌ام داخل پلتفرم{" "}
            <b className="text-foreground">ترندی اسمارت</b>. تحلیل می‌کنم، پیشنهاد می‌دم، جواب
            مشتری‌هاتو می‌دم و حواسم به پیجت هست.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              href="/auth"
              className="group relative inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-l from-primary-main to-emerald-500 text-white font-black text-sm overflow-hidden shadow-[0_24px_50px_-15px_rgba(0,150,136,.6)] hover:shadow-[0_28px_60px_-12px_rgba(0,150,136,.75)] transition-all hover:-translate-y-0.5"
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full transition-transform duration-700" />
              <Sparkles size={16} className="relative" />
              <span className="relative">{persona.name} رو فعال کن</span>
              <ArrowLeft
                size={16}
                className="relative group-hover:-translate-x-1 transition-transform"
              />
            </Link>
            <a
              href="#demo"
              className="group inline-flex items-center gap-2 px-6 py-4 rounded-2xl border border-border bg-card/60 backdrop-blur text-sm font-bold hover:border-primary-main/40 hover:bg-card transition-all"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full bg-primary-main/15 text-primary-main">
                <PlayCircle size={14} />
              </span>
              ببین چطور کار می‌کنه
            </a>
          </div>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex -space-x-2 -space-x-reverse">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-9 w-9 rounded-full border-2 border-background bg-gradient-to-br from-primary-main to-secondary-main shadow grid place-items-center text-[10px] font-black text-white"
                >
                  {["ع", "م", "ر", "س"][i]}
                </div>
              ))}
            </div>
            <div className="text-[12px] leading-5">
              <p className="font-bold">+۲٬۴۰۰ کسب‌وکار فعال</p>
              <p className="text-muted-foreground">هر روز با ترندی هوشمندتر می‌شن</p>
            </div>
          </div>
        </motion.div>

        {/* Mascot stage — calm, single focal point */}
        <motion.div
          style={{ y: yMascot }}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="relative mx-auto h-[480px] w-full max-w-[460px]"
        >
          {/* concentric soft rings — pure CSS, no rotation/distraction */}
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="h-[420px] w-[420px] rounded-full border border-primary-main/10" />
          </div>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="h-[320px] w-[320px] rounded-full border border-primary-main/15" />
          </div>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div
              className="h-[260px] w-[260px] rounded-full opacity-70 blur-3xl"
              style={{
                background:
                  "radial-gradient(circle, rgba(0,150,136,.55), rgba(254,150,74,.18) 55%, transparent 75%)",
              }}
            />
          </div>

          {/* mascot — slow gentle float only */}
          <div className="relative h-full w-full grid place-items-center">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <TrendiMascot size={340} static withGlow={false} withBadge={false} />
            </motion.div>

            {/* one quiet status pill */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="absolute bottom-4 inline-flex items-center gap-2 rounded-full border border-border bg-card/90 backdrop-blur px-3.5 py-1.5 shadow-lg"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
                <span className="relative h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11.5px] font-bold text-foreground">آنلاین، آماده‌ی کمک</span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FloatCard({
  className,
  delay,
  icon: Icon,
  title,
  sub,
  tone,
}: {
  className?: string;
  delay: number;
  icon: any;
  title: string;
  sub: string;
  tone: "primary" | "secondary" | "success" | "alert";
}) {
  const toneMap = {
    primary: { bg: "bg-primary-main/15", text: "text-primary-main", ring: "ring-primary-main/30" },
    secondary: {
      bg: "bg-secondary-main/15",
      text: "text-secondary-main",
      ring: "ring-secondary-main/30",
    },
    success: { bg: "bg-emerald-500/15", text: "text-emerald-500", ring: "ring-emerald-500/30" },
    alert: { bg: "bg-orange-500/15", text: "text-orange-500", ring: "ring-orange-500/30" },
  } as const;
  const t = toneMap[tone];
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay, duration: 0.6, ease: "easeOut" }}
      className={cn("z-10", className)}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4 + delay, repeat: Infinity, ease: "easeInOut" }}
        className={cn(
          "flex items-center gap-3 rounded-2xl bg-card/90 backdrop-blur-xl border border-border ring-1 px-3.5 py-2.5 shadow-[0_15px_30px_-10px_rgba(0,0,0,0.15)]",
          t.ring,
        )}
      >
        <span className={cn("grid h-9 w-9 place-items-center rounded-xl", t.bg, t.text)}>
          <Icon size={16} />
        </span>
        <div className="leading-tight">
          <p className="text-[12px] font-black text-foreground whitespace-nowrap">{title}</p>
          <p className="text-[10.5px] text-muted-foreground whitespace-nowrap">{sub}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ============================================================
   STATS BAR
   ============================================================ */
function StatsBar() {
  const stats = [
    { v: "۹۸٪", l: "نرخ پاسخ خودکار" },
    { v: "۳ ثانیه", l: "میانگین زمان پاسخ" },
    { v: "+۲۴٪", l: "افزایش تعامل دایرکت" },
    { v: "۲۴/۷", l: "آنلاین، بدون استراحت" },
  ];
  return (
    <section className="relative py-10 border-y border-border/60 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((s, i) => (
          <motion.div
            key={s.l}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
            transition={{ delay: i * 0.1 }}
            className="text-center"
          >
            <p className="text-3xl md:text-4xl font-black brand-gradient-text">{s.v}</p>
            <p className="mt-1.5 text-[12px] text-muted-foreground">{s.l}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   MEET TRENDI
   ============================================================ */
function MeetTrendi() {
  return (
    <Section id="meet" eyebrow="آشنایی" title="با ترندی آشنا شو" sub="یک شخصیت هوشمند، نه یک API.">
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(0,150,136,.3),transparent_70%)] blur-3xl" />
          <div className="relative grid place-items-center p-8 rounded-[40px] border border-border bg-gradient-to-br from-muted/40 to-transparent">
            <TrendiMascot size={280} mood="happy" />
          </div>
          {/* floating tags */}
          <FloatTag className="absolute top-4 -right-3" icon={Heart} text="صمیمی" />
          <FloatTag className="absolute bottom-12 -left-3" icon={Brain} text="هوشمند" />
          <FloatTag className="absolute -bottom-2 right-12" icon={ShieldCheck} text="مطمئن" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3 className="text-3xl md:text-4xl font-black leading-tight">
            یه دستیار که پیجتو می‌فهمه،
            <br />
            با لحن <span className="brand-gradient-text">برند خودت</span> حرف می‌زنه.
          </h3>
          <p className="mt-5 text-muted-foreground leading-9">
            ترندی از پست‌ها، استوری‌ها و گفتگوهای قبلی پیجت یاد می‌گیره و طوری جواب می‌ده که
            مشتری‌ها فکر کنن دارن با خودِ تیم برند حرف می‌زنن. هرچی بیشتر باهاش کار کنی، بهتر هم
            می‌شه.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {[
              { icon: Wand2, t: "آموزش با محتوای تو" },
              { icon: Languages, t: "فارسی محاوره‌ای روان" },
              { icon: Eye, t: "حضور contextual همه‌جا" },
              { icon: Palette, t: "نام و رنگ اختصاصی" },
            ].map((it) => (
              <div
                key={it.t}
                className="flex items-center gap-3 rounded-2xl border border-white/8 bg-muted/30 p-3"
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary-main/15 text-primary-main">
                  <it.icon size={16} />
                </span>
                <span className="text-[13px] font-bold text-foreground">{it.t}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function FloatTag({
  icon: Icon,
  text,
  className,
}: {
  icon: any;
  text: string;
  className?: string;
}) {
  return (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      className={cn(
        "flex items-center gap-1.5 rounded-full border border-border bg-card/90 backdrop-blur px-3 py-1.5 shadow-xl",
        className,
      )}
    >
      <Icon size={12} className="text-secondary-main" />
      <span className="text-[11px] font-bold">{text}</span>
    </motion.div>
  );
}

/* ============================================================
   PERSONALITY
   ============================================================ */
function Personality() {
  const traits = [
    {
      icon: Heart,
      name: "صمیمی",
      desc: "مثل یه دوست با مشتری حرف می‌زنم، نه یه ربات.",
      color: "from-red-500 to-secondary-main",
    },
    {
      icon: Lightbulb,
      name: "کنجکاو",
      desc: "هر پیام جدید برام یه فرصت یادگیریه.",
      color: "from-secondary-main to-amber",
    },
    {
      icon: ShieldCheck,
      name: "محافظ",
      desc: "مراقب اکانت و دیتای کسب‌وکارتم.",
      color: "from-primary-main to-blue",
    },
    {
      icon: Rocket,
      name: "بلندپرواز",
      desc: "هدفم رشد پیج توئه، نه فقط جواب دادن.",
      color: "from-blue to-purple",
    },
  ];
  return (
    <Section
      id="personality"
      eyebrow="شخصیت من"
      title="من یه شخصیت دارم، نه یه API"
      sub="چهار صفت اصلی که ترندی رو تعریف می‌کنه."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {traits.map((t, i) => (
          <motion.div
            key={t.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
            transition={{ delay: i * 0.1 }}
            className="group relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-muted/40 to-transparent p-6 hover:border-primary-main/40 transition-all"
          >
            <div
              className={cn(
                "absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-30 blur-3xl bg-gradient-to-br",
                t.color,
              )}
            />
            <div
              className={cn(
                "relative grid h-14 w-14 place-items-center rounded-2xl text-white shadow-xl bg-gradient-to-br",
                t.color,
              )}
            >
              <t.icon size={24} />
            </div>
            <p className="mt-4 text-xl font-black">{t.name}</p>
            <p className="mt-2 text-[13px] leading-7 text-muted-foreground">{t.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================
   ABILITIES — bento grid
   ============================================================ */
function Abilities() {
  return (
    <Section
      id="abilities"
      eyebrow="توانایی‌ها"
      title="چیکارا می‌تونم بکنم؟"
      sub="یه نگاه کامل به مهارت‌های ترندی."
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-[180px]">
        <BentoCard
          className="md:col-span-3 md:row-span-2"
          gradient="from-primary-main/30 via-primary-main/10 to-transparent"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary-main/20 text-primary-main">
              <Brain size={22} />
            </span>
            <span className="text-[10px] font-bold text-primary-main border border-primary-main/40 rounded-full px-2 py-0.5">
              CORE
            </span>
          </div>
          <h4 className="mt-4 text-2xl font-black">تحلیل عمیق رفتار مشتری</h4>
          <p className="mt-2 text-[13px] text-muted-foreground leading-7">
            رفتار مشتری، نرخ تعامل، الگوهای پاسخ، بهترین زمان ارسال — همه‌چی رو زیر نظر دارم و از
            داده‌های پیج یاد می‌گیرم.
          </p>
          <div className="mt-6 flex gap-2 flex-wrap">
            {["Sentiment", "Engagement", "Funnel", "Heatmap"].map((t) => (
              <span
                key={t}
                className="text-[10.5px] font-bold px-2.5 py-1 rounded-full bg-muted/40 border border-border"
              >
                {t}
              </span>
            ))}
          </div>
        </BentoCard>

        <BentoCard className="md:col-span-3" gradient="from-secondary-main/25 to-transparent">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-secondary-main/20 text-secondary-main">
              <MessageCircle size={20} />
            </span>
            <h4 className="text-lg font-black">پاسخ خودکار به دایرکت</h4>
          </div>
          <p className="mt-2 text-[12.5px] text-muted-foreground leading-7">
            با لحن برندت به دایرکت‌ها جواب می‌دم و سفارش می‌گیرم.
          </p>
        </BentoCard>

        <BentoCard className="md:col-span-3" gradient="from-amber/25 to-transparent">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-amber/20 text-amber">
              <Bell size={20} />
            </span>
            <h4 className="text-lg font-black">هشدار هوشمند</h4>
          </div>
          <p className="mt-2 text-[12.5px] text-muted-foreground leading-7">
            افزایش ناگهانی پیام، خطر بلاک، صف داغ — همه رو سریع بهت می‌گم.
          </p>
        </BentoCard>

        <BentoCard className="md:col-span-2" gradient="from-blue/25 to-transparent">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue/20 text-blue">
            <TrendingUp size={20} />
          </span>
          <h4 className="mt-3 text-base font-black">پیشنهاد بهینه‌سازی</h4>
          <p className="mt-1 text-[12px] text-muted-foreground leading-6">
            پاسخ‌های قوی‌تر و زمان ارسال بهتر.
          </p>
        </BentoCard>

        <BentoCard className="md:col-span-2" gradient="from-primary-main/20 to-transparent">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary-main/20 text-primary-main">
            <ShieldCheck size={20} />
          </span>
          <h4 className="mt-3 text-base font-black">مراقبت از اکانت</h4>
          <p className="mt-1 text-[12px] text-muted-foreground leading-6">رعایت محدودیت‌های API.</p>
        </BentoCard>

        <BentoCard className="md:col-span-2" gradient="from-purple/20 to-transparent">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-purple/20 text-purple">
            <Zap size={20} />
          </span>
          <h4 className="mt-3 text-base font-black">یادگیری مداوم</h4>
          <p className="mt-1 text-[12px] text-muted-foreground leading-6">
            از هر گفتگو بهتر می‌شم.
          </p>
        </BentoCard>
      </div>
    </Section>
  );
}

function BentoCard({
  children,
  className,
  gradient,
}: {
  children: React.ReactNode;
  className?: string;
  gradient?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.5 }}
      className={cn(
        "relative overflow-hidden rounded-3xl border border-border bg-card/60 p-5 hover:border-primary-main/40 transition-colors",
        className,
      )}
    >
      {gradient && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 bg-gradient-to-br opacity-60",
            gradient,
          )}
        />
      )}
      <div className="relative">{children}</div>
    </motion.div>
  );
}

/* ============================================================
   HOW IT WORKS
   ============================================================ */
function HowItWorks() {
  const steps = [
    {
      n: "۰۱",
      icon: Instagram,
      title: "اتصال پیج اینستاگرام",
      desc: "در کمتر از ۲ دقیقه با OAuth رسمی متا.",
    },
    {
      n: "۰۲",
      icon: Brain,
      title: "آموزش با محتوای تو",
      desc: "از پست‌ها، استوری‌ها و گفتگوهای قبلی یاد می‌گیرم.",
    },
    {
      n: "۰۳",
      icon: MessageCircle,
      title: "جواب دادن هوشمند",
      desc: "دایرکت و کامنت‌ها رو با لحن برندت پاسخ می‌دم.",
    },
    {
      n: "۰۴",
      icon: BarChart3,
      title: "پایش و بهینه‌سازی",
      desc: "هر هفته پیشنهاد بهبود می‌فرستم.",
    },
  ];
  return (
    <Section
      id="how"
      eyebrow="چطور کار می‌کنم؟"
      title="چهار قدم تا پیج هوشمند"
      sub="از اتصال تا اولین پاسخ خودکار، در کمتر از ۵ دقیقه."
    >
      <div className="relative">
        <div className="absolute right-8 left-8 top-12 hidden md:block h-px bg-gradient-to-l from-transparent via-primary-main/40 to-transparent" />
        <div className="grid md:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
              transition={{ delay: i * 0.15 }}
              className="relative rounded-3xl border border-border bg-gradient-to-br from-muted/40 to-transparent p-5"
            >
              <div className="flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-primary-main to-primary-300 text-white shadow-lg">
                  <s.icon size={20} />
                </span>
                <span className="font-mono text-3xl font-black text-foreground/10">{s.n}</span>
              </div>
              <p className="mt-4 text-[15px] font-bold">{s.title}</p>
              <p className="mt-1.5 text-[12.5px] leading-7 text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   LIVE CONVERSATION DEMO
   ============================================================ */
function LiveConversation() {
  const { persona } = useAiPersona();
  return (
    <Section
      id="demo"
      eyebrow="دموی زنده"
      title={`${persona.name} با مشتریت چجوری حرف می‌زنه؟`}
      sub="یه پیش‌نمایش واقعی از یک گفتگوی دایرکت اینستاگرام."
    >
      <div className="grid lg:grid-cols-[1fr_1.05fr] gap-10 items-center">
        {/* Left: features */}
        <div className="space-y-4">
          {[
            { icon: Wand2, t: "لحن برند خودت", d: "از روی پست‌های قبلیت لحن منو می‌سازی." },
            { icon: Zap, t: "پاسخ در ۳ ثانیه", d: "مشتری منتظر نمی‌مونه." },
            { icon: Heart, t: "احساس انسانی", d: "از Emoji و خودمونی بودن نمی‌ترسم." },
            { icon: ShieldCheck, t: "Handover به آدم", d: "هرجا لازم بود به اپراتور پاس می‌دم." },
          ].map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
              transition={{ delay: i * 0.08 }}
              className="flex items-start gap-4 rounded-2xl border border-border bg-muted/30 p-4"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary-main/15 text-primary-main shrink-0">
                <f.icon size={18} />
              </span>
              <div>
                <p className="text-[14px] font-bold">{f.t}</p>
                <p className="mt-0.5 text-[12px] text-muted-foreground leading-7">{f.d}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Right: phone mockup */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
          transition={{ duration: 0.6 }}
          className="mx-auto w-full max-w-[400px]"
        >
          <div className="relative rounded-[44px] border-[10px] border-border bg-background shadow-[0_40px_80px_-20px_rgba(0,150,136,.4)] overflow-hidden">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-28 rounded-full bg-card z-10" />
            <div className="bg-gradient-to-b from-grayScale-800 to-grayScale-900 p-4 pt-8">
              {/* DM header */}
              <div className="flex items-center gap-2 pb-3 border-b border-border">
                <TrendiMascot size={36} mood="happy" withGlow={false} />
                <div>
                  <p className="text-[13px] font-bold">{persona.name}</p>
                  <p className="text-[10px] text-primary-main flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary-main animate-pulse" />{" "}
                    آنلاین
                  </p>
                </div>
                <Instagram size={16} className="ms-auto text-grayScale-500" />
              </div>

              {/* Messages */}
              <div className="space-y-3 py-4 min-h-[420px]">
                <Msg side="user">سلام، شلوار سایز ۳۲ موجوده؟</Msg>
                <Msg side="bot">
                  سلام! 👋 آره عزیزم، سایز ۳۲ از کالکشن جدید موجوده. می‌خوای جدول سایزو بفرستم؟
                </Msg>
                <Msg side="user">آره ممنون</Msg>
                <Msg side="bot">
                  اینم جدول سایز 📏 اگه دور کمرت بین ۸۰ تا ۸۴ سانته، سایز ۳۲ مناسبته. سفارش بدم؟
                </Msg>
                <div className="flex gap-2 flex-wrap">
                  <QuickReply>بله، ثبت کن</QuickReply>
                  <QuickReply>عکس بیشتر</QuickReply>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-grayScale-500 pt-1">
                  <span className="flex gap-0.5">
                    <span className="h-1 w-1 rounded-full bg-primary-main animate-bounce" />
                    <span
                      className="h-1 w-1 rounded-full bg-primary-main animate-bounce"
                      style={{ animationDelay: "150ms" }}
                    />
                    <span
                      className="h-1 w-1 rounded-full bg-primary-main animate-bounce"
                      style={{ animationDelay: "300ms" }}
                    />
                  </span>
                  {persona.name} در حال نوشتن…
                </div>
              </div>

              {/* Input */}
              <div className="flex items-center gap-2 pt-2 border-t border-border">
                <input
                  className="flex-1 bg-card/60 rounded-full px-4 py-2 text-[12px] outline-none"
                  placeholder="پیام بنویس…"
                  disabled
                />
                <button className="h-9 w-9 rounded-full bg-primary-main text-white grid place-items-center">
                  <ArrowLeft size={14} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function Msg({ side, children }: { side: "user" | "bot"; children: React.ReactNode }) {
  if (side === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-tre-md bg-muted px-3 py-2 text-[12.5px] text-foreground">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-[78%] rounded-2xl rounded-ts-md bg-gradient-to-l from-primary-main to-primary-300 px-3 py-2 text-[12.5px] text-white shadow">
        {children}
      </div>
    </div>
  );
}

function QuickReply({ children }: { children: React.ReactNode }) {
  return (
    <button className="rounded-full border border-primary-main/40 bg-primary-main/10 px-3 py-1 text-[11px] font-bold text-primary-main hover:scale-[1.03] transition-transform">
      {children}
    </button>
  );
}

/* ============================================================
   WHITE LABEL
   ============================================================ */
interface BrandSpec {
  name: string;
  latin: string;
  role: string;
  tone: string;
  accent: "orange" | "amber" | "blue" | "teal";
  stats: { k: string; v: string }[];
  chats: { user: string; ai: string }[][]; // multiple sets, cycle on click
}

const BRANDS: BrandSpec[] = [
  {
    name: "آوا",
    latin: "Ava",
    role: "فروشگاه پوشاک Studio Avi",
    tone: "صمیمی و گرم",
    accent: "orange",
    stats: [
      { k: "+۳۸٪", v: "تبدیل دایرکت" },
      { k: "۲s", v: "زمان پاسخ" },
    ],
    chats: [
      [
        {
          user: "این مانتو سایز فری داره؟",
          ai: "سلام عزیز ✨ آره! سایز فری تا ۴۲ تنخور داره. اگه بخوای الان لینکشو می‌فرستم 💛",
        },
      ],
      [
        {
          user: "رنگش زنده‌ست؟",
          ai: "آره جون من خیلی شیک افتاده 😍 یه استوری گذاشتم همین الان، یه سر بزن!",
        },
      ],
      [
        {
          user: "ارسال چقدر طول می‌کشه؟",
          ai: "فردا برات می‌فرستیم رفیق ✨ اگه پرداختت تا ۱۰ شب باشه، صبح زود ارسال می‌شه 💛",
        },
      ],
    ],
  },
  {
    name: "مایا",
    latin: "Maya",
    role: "برند زیبایی Maya Beauty",
    tone: "شیطون و انرژیک",
    accent: "amber",
    stats: [
      { k: "+۲۴٪", v: "ذخیره پست" },
      { k: "۴٬۲", v: "DM روزانه" },
    ],
    chats: [
      [
        {
          user: "این رژ روی پوست گندمی خوبه؟",
          ai: "اوهو 😍 دقیقاً برای پوست گندمی ساخته شده! تستش رو بذار، عاشقش می‌شی.",
        },
      ],
      [
        {
          user: "ضدآبه؟",
          ai: "صد در صد! تا ۸ ساعت سرجاش می‌مونه 💋 حتی پای قهوه‌ی صبح هم خم به ابرو نمیاره ✨",
        },
      ],
      [{ user: "تخفیف داری؟", ai: "آره بابا 🎉 با کد MAYA20 بیست درصد می‌خوره — فقط یادت نره 😉" }],
    ],
  },
  {
    name: "دکترینا",
    latin: "Doctorina",
    role: "کلینیک پوست و مو",
    tone: "حرفه‌ای و رسمی",
    accent: "blue",
    stats: [
      { k: "+۵۲٪", v: "نوبت آنلاین" },
      { k: "۱۰۰٪", v: "پاسخ‌گویی" },
    ],
    chats: [
      [
        {
          user: "برای رفع لک قیمت تقریبی چقدره؟",
          ai: "سلام، وقت بخیر. هزینه پس از معاینه‌ی اولیه برآورد می‌شود. آیا تمایل دارید نوبت رزرو کنم؟",
        },
      ],
      [
        {
          user: "متخصص خانم دارید؟",
          ai: "بله، تیم ما متشکل از پزشکان متخصص آقا و خانم است. زمان مراجعه‌ی مدنظر شما؟",
        },
      ],
      [
        {
          user: "آیا مشاوره‌ی آنلاین دارید؟",
          ai: "بله، مشاوره‌ی تصویری ۱۵ دقیقه‌ای رایگان ارائه می‌دهیم. لطفاً ساعت مناسب را اعلام بفرمایید.",
        },
      ],
    ],
  },
];

function WhiteLabel() {
  return (
    <Section
      eyebrow="White-label"
      title="هر کسب‌وکار، ترندی خودش رو داره"
      sub="نام، لحن، رنگ و شخصیت AI رو خودت انتخاب می‌کنی. روی هر کارت کلیک کن تا نمونه‌های گفتگوی جدید با لحن همان برند رو ببینی."
    >
      <div className="grid md:grid-cols-3 gap-6">
        {BRANDS.map((b, i) => (
          <BrandCard key={b.name} brand={b} delay={i * 0.12} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        className="mt-10 flex flex-wrap items-center justify-center gap-3 text-center"
      >
        <p className="text-[13px] text-muted-foreground">
          + پشتیبانی از هر تعداد workspace با شخصیت‌های مستقل
        </p>
        <a
          href="#studio"
          className="inline-flex items-center gap-1.5 text-[13px] font-black text-primary-main hover:gap-2.5 transition-all"
        >
          شخصیت AI خودت رو همینجا بساز <ArrowLeft size={14} />
        </a>
      </motion.div>
    </Section>
  );
}

function BrandCard({ brand: b, delay }: { brand: BrandSpec; delay: number }) {
  const [chatIdx, setChatIdx] = useState(0);
  const main = ACCENT_HEX[b.accent];
  const soft = ACCENT_HEX_SOFT[b.accent];
  const totalSets = b.chats.length;
  const currentChat = b.chats[chatIdx];

  const next = () => setChatIdx((v) => (v + 1) % totalSets);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay, duration: 0.6 }}
      className="group relative rounded-[28px] p-[1.5px] transition-transform hover:-translate-y-1.5 duration-500"
      style={{ background: `linear-gradient(160deg, ${main}55, transparent 40%, ${soft}55)` }}
    >
      <button
        onClick={next}
        type="button"
        className="block w-full text-start cursor-pointer"
        aria-label={`نمایش گفتگوی بعدی ${b.name}`}
      >
        <div className="relative h-full rounded-[26px] bg-card overflow-hidden shadow-[0_10px_40px_-20px_rgba(0,0,0,0.18)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.6)]">
          {/* header banner */}
          <div
            className="relative h-[120px] overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
          >
            <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.2)_1px,transparent_1px)] [background-size:20px_20px]" />
            <div className="absolute -top-12 -end-12 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
            <div className="relative h-full flex items-end justify-between p-4">
              <span className="rounded-full bg-white/20 backdrop-blur px-2.5 py-1 text-[10px] font-black text-white border border-white/30">
                {b.latin}
              </span>
              <span className="rounded-full bg-black/20 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white border border-white/20 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                آنلاین
              </span>
            </div>
          </div>

          {/* avatar overlapping */}
          <div className="px-5 -mt-10 relative">
            <div
              className="inline-grid place-items-center h-20 w-20 rounded-2xl shadow-xl ring-4 ring-card"
              style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
            >
              <TrendiMascot size={64} mood="happy" withGlow={false} withBadge={false} />
            </div>
            <div className="mt-3">
              <p className="text-xl font-black leading-tight text-foreground">{b.name}</p>
              <p className="text-[12px] text-muted-foreground mt-0.5">{b.role}</p>
            </div>
          </div>

          {/* stats */}
          <div className="px-5 mt-4 grid grid-cols-2 gap-2">
            {b.stats.map((s) => (
              <div key={s.v} className="rounded-xl bg-muted border border-border px-3 py-2">
                <p className="text-[15px] font-black" style={{ color: main }}>
                  {s.k}
                </p>
                <p className="text-[10.5px] text-muted-foreground mt-0.5">{s.v}</p>
              </div>
            ))}
          </div>

          {/* tone tag */}
          <div className="px-5 mt-4">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold border"
              style={{ color: main, borderColor: `${main}55`, background: `${main}10` }}
            >
              <Wand2 size={10} /> لحن: {b.tone}
            </span>
          </div>

          {/* mini chat preview — animated, cycles on click */}
          <div className="px-5 mt-4 pb-3 space-y-2 min-h-[140px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={chatIdx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="space-y-2"
              >
                {currentChat.map((m, idx) => (
                  <React.Fragment key={idx}>
                    <div className="flex">
                      <div className="max-w-[80%] rounded-2xl rounded-bs-md bg-muted px-3 py-2 text-[12px] leading-6 text-foreground">
                        {m.user}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div
                        className="max-w-[85%] rounded-2xl rounded-be-md px-3 py-2 text-[12px] leading-6 text-white shadow-md"
                        style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
                      >
                        {m.ai}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* footer cycle indicator */}
          <div className="px-5 pb-4 flex items-center justify-between">
            <div className="flex gap-1.5">
              {b.chats.map((_, k) => (
                <span
                  key={k}
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: k === chatIdx ? 18 : 6,
                    background: k === chatIdx ? main : "rgb(var(--muted-foreground) / 0.3)",
                    backgroundColor: k === chatIdx ? main : undefined,
                  }}
                />
              ))}
            </div>
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold text-muted-foreground group-hover:text-primary-main transition-colors">
              <Sparkles size={10} /> پیام بعدی
            </span>
          </div>
        </div>
      </button>
    </motion.div>
  );
}

/* ============================================================
   PERSONA STUDIO — live AI persona customizer
   ============================================================ */
type StudioAccent = "teal" | "orange" | "amber" | "blue";
type StudioTone = "friendly" | "formal" | "playful";

const STUDIO_ACCENTS: { key: StudioAccent; label: string }[] = [
  { key: "teal", label: "تیل" },
  { key: "orange", label: "نارنجی" },
  { key: "amber", label: "کهربایی" },
  { key: "blue", label: "آبی" },
];
const STUDIO_TONES: { key: StudioTone; label: string; sample: string }[] = [
  {
    key: "friendly",
    label: "صمیمی",
    sample: "سلام رفیق ✨ خوش اومدی! هر سوالی داری بپرس، در خدمتم 💛",
  },
  {
    key: "playful",
    label: "شیطون",
    sample: "اوه‌اوه یه مهمون جدید 😍 بیا یه چیز باحال نشونت بدم 🎉",
  },
  {
    key: "formal",
    label: "رسمی",
    sample: "سلام، وقت بخیر. خوش‌آمدید. در چه زمینه‌ای می‌توانم به شما کمک کنم؟",
  },
];

function PersonaStudio() {
  const [name, setNm] = useState("ترندی");
  const [latin, setLat] = useState("Trendi");
  const [tone, setTn] = useState<StudioTone>("friendly");
  const [accent, setAc] = useState<StudioAccent>("teal");

  const main = ACCENT_HEX[accent];
  const soft = ACCENT_HEX_SOFT[accent];
  const sample = STUDIO_TONES.find((t) => t.key === tone)!.sample;

  return (
    <Section
      id="studio"
      eyebrow="استودیو شخصیت"
      title="AI خودت رو در ۳۰ ثانیه بساز"
      sub="نام، لحن و رنگ AI کسب‌وکارتو انتخاب کن و همین لحظه پیش‌نمایششو ببین."
    >
      <div className="grid lg:grid-cols-[1fr_1.05fr] gap-8 items-start">
        {/* Controls */}
        <div className="rounded-3xl border border-border bg-card p-6 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.15)]">
          {/* names */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-bold text-muted-foreground mb-1.5">
                نام فارسی
              </label>
              <input
                value={name}
                onChange={(e) => setNm(e.target.value)}
                placeholder="مثلاً آوا"
                className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[14px] font-bold focus:outline-none focus:ring-2 focus:ring-primary-main"
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-muted-foreground mb-1.5">
                نام لاتین
              </label>
              <input
                value={latin}
                onChange={(e) => setLat(e.target.value)}
                placeholder="Ava"
                className="w-full rounded-xl border border-border bg-background px-3.5 py-2.5 text-[14px] font-bold focus:outline-none focus:ring-2 focus:ring-primary-main"
              />
            </div>
          </div>

          {/* tone */}
          <div className="mt-5">
            <label className="block text-[12px] font-bold text-muted-foreground mb-1.5">
              لحن گفتگو
            </label>
            <div className="grid grid-cols-3 gap-2">
              {STUDIO_TONES.map((t) => {
                const active = tone === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTn(t.key)}
                    className={cn(
                      "rounded-xl border px-3 py-2.5 text-[13px] font-bold transition-all",
                      active
                        ? "text-white shadow-md"
                        : "bg-muted border-border text-foreground hover:border-primary-main/40",
                    )}
                    style={
                      active
                        ? {
                            background: `linear-gradient(135deg, ${main}, ${soft})`,
                            borderColor: main,
                          }
                        : undefined
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* accent */}
          <div className="mt-5">
            <label className="block text-[12px] font-bold text-muted-foreground mb-1.5">
              رنگ برند
            </label>
            <div className="flex gap-2.5">
              {STUDIO_ACCENTS.map((a) => {
                const active = accent === a.key;
                const c = ACCENT_HEX[a.key];
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setAc(a.key)}
                    aria-label={a.label}
                    className={cn(
                      "relative h-11 w-11 rounded-2xl ring-2 transition-all",
                      active ? "ring-foreground/40 scale-105" : "ring-transparent hover:scale-105",
                    )}
                    style={{
                      background: `linear-gradient(135deg, ${c}, ${ACCENT_HEX_SOFT[a.key]})`,
                    }}
                  >
                    {active && <Check size={16} className="absolute inset-0 m-auto text-white" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* summary */}
          <div className="mt-6 rounded-2xl border border-dashed border-border bg-muted/40 p-3.5 text-[12px] leading-7 text-muted-foreground">
            هوش مصنوعی این کسب‌وکار با نام
            <b className="text-foreground"> {name || "—"} </b>و لحن
            <b className="text-foreground"> {STUDIO_TONES.find((t) => t.key === tone)?.label} </b>
            با مشتری‌ها صحبت می‌کنه.
          </div>
        </div>

        {/* Live preview */}
        <div
          className="relative rounded-3xl p-[1.5px] shadow-[0_30px_60px_-30px_rgba(0,0,0,0.25)]"
          style={{ background: `linear-gradient(160deg, ${main}66, transparent 40%, ${soft}66)` }}
        >
          <div className="rounded-[22px] bg-card overflow-hidden">
            {/* banner */}
            <div
              className="relative h-[110px]"
              style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
            >
              <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.2)_1px,transparent_1px)] [background-size:20px_20px]" />
              <div className="absolute top-4 end-4 rounded-full bg-white/20 backdrop-blur border border-white/30 px-2.5 py-1 text-[10px] font-black text-white inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" /> پیش‌نمایش
                زنده
              </div>
            </div>

            <div className="px-5 -mt-10 pb-5">
              <div
                className="inline-grid place-items-center h-20 w-20 rounded-2xl shadow-xl ring-4 ring-card"
                style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
              >
                <TrendiMascot size={64} mood="happy" withGlow={false} withBadge={false} />
              </div>
              <div className="mt-3 flex items-end gap-2">
                <p className="text-2xl font-black leading-tight text-foreground">{name || "—"}</p>
                <p className="text-[12px] text-muted-foreground mb-1">/ {latin || "—"}</p>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`${tone}-${accent}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 space-y-2"
                >
                  <div className="flex">
                    <div className="max-w-[80%] rounded-2xl rounded-bs-md bg-muted px-3 py-2 text-[12.5px] leading-6 text-foreground">
                      سلام، شما همون {name || "هوش مصنوعی"} هستید؟
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div
                      className="max-w-[88%] rounded-2xl rounded-be-md px-3.5 py-2 text-[12.5px] leading-6 text-white shadow-md"
                      style={{ background: `linear-gradient(135deg, ${main}, ${soft})` }}
                    >
                      {sample}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-5 flex items-center gap-2 text-[11px] text-muted-foreground">
                <Palette size={12} style={{ color: main }} />
                <span>این تنظیمات داخل داشبورد هر workspace قابل ذخیره‌ست.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ============================================================
   COMPARISON TABLE
   ============================================================ */
function Comparison() {
  const rows = [
    { feat: "پاسخ خودکار به دایرکت", trendi: true, manual: false },
    { feat: "پاسخ خودکار به کامنت‌ها", trendi: true, manual: false },
    { feat: "تحلیل احساسات پیام", trendi: true, manual: false },
    { feat: "پاسخ ۲۴/۷ بدون وقفه", trendi: true, manual: false },
    { feat: "یادگیری لحن و سبک برند", trendi: true, manual: false },
    { feat: "هشدار افزایش ناگهانی پیام", trendi: true, manual: false },
    { feat: "پاسخ همزمان به ۱۰۰۰+ نفر", trendi: true, manual: false },
    { feat: "تشخیص سؤال تکراری و پاسخ یکسان", trendi: true, manual: false },
    { feat: "ارسال لینک پرداخت / شماره کارت", trendi: true, manual: true },
    { feat: "تأیید خودکار فیش واریزی", trendi: true, manual: false },
    { feat: "ثبت سفارش و ارسال کد پیگیری", trendi: true, manual: true },
    { feat: "گزارش آماری از مکالمات", trendi: true, manual: false },
    { feat: "پیشنهاد محصول مرتبط به مشتری", trendi: true, manual: false },
    { feat: "Handover به اپراتور انسانی", trendi: true, manual: true },
    { feat: "پشتیبانی چند زبانه", trendi: true, manual: false },
    { feat: "فعالیت بدون نیاز به آموزش نیرو", trendi: true, manual: false },
    { feat: "هزینه استخدام نیرو", trendi: false, manual: true },
  ];
  return (
    <Section
      eyebrow="مقایسه"
      title="ترندی در مقایسه با مدیریت دستی"
      sub="یه نگاه شفاف به اینکه چی به دست میاری."
    >
      <div className="overflow-hidden rounded-3xl border border-border bg-card/60">
        <table className="w-full text-start text-[13px]">
          <thead className="bg-muted/40">
            <tr>
              <th className="p-4 text-muted-foreground text-start font-bold">ویژگی</th>
              <th className="p-4 text-center">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold text-white bg-gradient-to-l from-primary-main to-secondary-main">
                  <Sparkles size={11} /> ترندی
                </span>
              </th>
              <th className="p-4 text-center text-muted-foreground font-bold">دستی</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.feat}
                className={cn("border-t border-border/60", i % 2 === 1 && "bg-muted/30")}
              >
                <td className="p-4 font-medium text-foreground">{r.feat}</td>
                <td className="p-4 text-center">
                  {r.trendi ? (
                    <Check size={20} className="inline text-primary-main" />
                  ) : (
                    <X size={20} className="inline text-grayScale-600" />
                  )}
                </td>
                <td className="p-4 text-center">
                  {r.manual ? (
                    <Check size={20} className="inline text-grayScale-500" />
                  ) : (
                    <X size={20} className="inline text-grayScale-700" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/* ============================================================
   BRAND SEPARATION
   ============================================================ */
// BrandSeparation removed per request.

/* ============================================================
   TRUST
   ============================================================ */
function Trust() {
  const items = [
    { icon: Lock, t: "رمزنگاری end-to-end", d: "تمام دیتای پیج‌ها امن نگه داشته می‌شه." },
    { icon: ShieldCheck, t: "مطابق سیاست متا", d: "هیچ‌وقت خطر بلاک شدن اکانت نیست." },
    { icon: Eye, t: "بدون اشتراک‌گذاری دیتا", d: "دیتای تو فقط برای تو استفاده می‌شه." },
  ];
  return (
    <section className="py-16 px-6">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
        {items.map((it) => (
          <div key={it.t} className="flex items-start gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-muted/40 text-primary-main border border-border">
              <it.icon size={20} />
            </span>
            <div>
              <p className="text-[14px] font-bold">{it.t}</p>
              <p className="mt-1 text-[12px] leading-7 text-muted-foreground">{it.d}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   FAQ
   ============================================================ */
function FAQ() {
  const faqs = [
    {
      q: "ترندی با اسم اختصاصی هم کار می‌کنه؟",
      a: "بله. هر workspace می‌تونه نام، لحن و رنگ AI خودش رو داشته باشه.",
    },
    {
      q: "آیا اکانتم بلاک نمی‌شه؟",
      a: "ترندی کاملاً مطابق سیاست‌های رسمی متا کار می‌کنه و throttling هوشمند داره.",
    },
    {
      q: "زبان فارسی رو خوب می‌فهمه؟",
      a: "بله. ترندی به طور خاص برای فارسی محاوره‌ای آموزش دیده.",
    },
    {
      q: "می‌تونم لحن جواب‌ها رو تغییر بدم؟",
      a: "سه لحن اصلی (صمیمی، رسمی، شیطون) داره و می‌تونی نمونه‌ی شخصی هم اضافه کنی.",
    },
    {
      q: "آیا گفتگو رو می‌تونم به اپراتور انسانی پاس بدم؟",
      a: "بله. ترندی هرجا لازم بدونه (یا تو دستور بدی)، گفتگو رو به تیمت Handover می‌کنه.",
    },
  ];
  return (
    <Section
      id="faq"
      eyebrow="سؤالات متداول"
      title="هرچی می‌خوای بدونی"
      sub="پاسخ سریع به رایج‌ترین سؤال‌ها."
    >
      <div className="max-w-3xl mx-auto space-y-3">
        {faqs.map((f, i) => (
          <FAQItem key={i} q={f.q} a={f.a} />
        ))}
      </div>
    </Section>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
      className="rounded-2xl border border-border bg-muted/30 overflow-hidden"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-5 text-start"
      >
        <p className="text-[14px] font-bold">{q}</p>
        <ChevronDown
          size={18}
          className={cn(
            "text-muted-foreground transition-transform",
            open && "rotate-180 text-primary-main",
          )}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-[13px] leading-8 text-muted-foreground border-t border-border/60 pt-4">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ============================================================
   FINAL CTA
   ============================================================ */
function FinalCTA() {
  const { persona } = useAiPersona();
  return (
    <section className="py-20 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
        className="max-w-5xl mx-auto relative overflow-hidden rounded-[40px] p-10 md:p-14 text-center bg-gradient-to-br from-primary-main via-primary-300 to-secondary-main"
      >
        <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(circle_at_20%_20%,rgba(255,255,255,.5),transparent_50%)]" />
        <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />

        <div className="relative flex flex-col items-center">
          <TrendiMascot size={140} mood="celebrating" />
          <h3 className="mt-6 text-3xl md:text-5xl font-black text-white">
            آماده‌ای {persona.name} رو فعال کنی؟
          </h3>
          <p className="mt-3 max-w-xl text-[14px] md:text-base leading-8 text-white/90">
            ۷ روز رایگان امتحان کن. خدمات فراتر از تصور. در کمتر از ۲ دقیقه پیجت هوشمند می‌شه.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/auth"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-white text-primary-main font-black text-sm shadow-2xl hover:scale-[1.04] transition-transform"
            >
              شروع رایگان <ArrowLeft size={16} />
            </Link>
            <a
              href="#demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl border border-white/60 text-white font-bold text-sm backdrop-blur hover:bg-white/10 transition-colors"
            >
              <Star size={16} /> دموی زنده
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ============================================================
   SECTION SHELL
   ============================================================ */
function Section({
  id,
  eyebrow,
  title,
  sub,
  children,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="relative py-20 md:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.05, margin: "0px 0px -10% 0px" }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-main/30 bg-primary-main/10 px-3 py-1 text-[11px] font-bold text-primary-main">
            <Layers size={11} /> {eyebrow}
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black leading-tight tracking-tight">
            {title}
          </h2>
          <p className="mt-3 text-[14px] md:text-[15px] leading-8 text-muted-foreground">{sub}</p>
        </motion.div>
        {children}
      </div>
    </section>
  );
}
